import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/Pages/dashboard.css";
import ClickImpressionBox from "../components/Dashboard/ClicksImpressionBox";
import MostViewedItem from "../components/Dashboard/MostViewedItem";
import AreaChartWithZoom from "../components/Dashboard/Chart";
import Dropdown from "../components/Common/Dropdown";
import { useSelector } from "react-redux";
import { Analytics } from "../Apis/analyticsApi";
import { Drawer, Spin, DatePicker } from "antd";
import StoreDrawer from "../components/Common/StoreDrawer";
import { getUserCookies } from "../utils/Cookies";
import { StatBox, withStats } from "../components/Dashboard/Statbox";
import { jwtDecode } from "jwt-decode";
import SubscriptionChart from "../components/Dashboard/SubscriptionChart";
import BusinessMetricsChart from "../components/Dashboard/BusinessMetricsChart";
import { translations } from "../utils/Translation/staticTranslation";
import { isProfileModuleReadOnly, effectiveHighestRole } from "../utils/modulePermissionSidebar";
import dayjs from "dayjs";
import MerchantSubscription from "../components/Dashboard/MerchantSubscription";
import MerAnalyticsCards from "../components/Dashboard/MerchantAnalyticsDashboard/MerAnalyticsCards";
import MerAnalyticsCharts from "../components/Dashboard/MerchantAnalyticsDashboard/MerAnalyticsCharts";
import MerchantGrowthTrend from "../components/Dashboard/MerchantAnalyticsDashboard/MerchantGrowthTrend";
import MerTable from "../components/Dashboard/MerchantAnalyticsDashboard/MerTable";
import DropdownWithDatePicker from "../components/Dashboard/DropdownWithDatePicker";
import FilterforMerchants from "../components/Dashboard/MerchantAnalyticsDashboard/FilterforMerchants";

const CLICKS_IMPRESSIONS_START_DATE = "2025-01-01";

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = getUserCookies()?.token;
  const decoded = token ? jwtDecode(token) : null;
  const highest_role = decoded?.highest_role;
  const market_role = decoded?.market_role;
  const user = useSelector((state) => state.user.user);
  const effectiveHighest = effectiveHighestRole(user, highest_role);
  const isSuperAdmin =
    effectiveHighest === "SUPERADMIN" ||
    highest_role === "SUPERADMIN" ||
    String(market_role ?? "") === "1" ||
    String(market_role ?? "") === "3";
  const lang = localStorage.getItem("language") || "ar";
  const currentLanguage = translations[lang]?.AnalyticsPages;
  const dashboardAdmin = translations[lang]?.DashboardSuperAdmin;
  const MerchantsCounts = withStats(StatBox);

  const [selectedDays, setSelectedDays] = useState(7);
  const [loading, setLoading] = useState(false);

  // 🔹 data for non-superadmin graphs
  const [metricsData, setMetricsData] = useState([]);

  // 🔹 merchant stats for SUPERADMIN cards - INDEPENDENT FILTER
  const [merchantCardData, setMerchantCardData] = useState(null);
  const [merchantCardLoading, setMerchantCardLoading] = useState(false);
  const [cardsFilterState, setCardsFilterState] = useState({
    startDate: null,
    endDate: null,
    filterType: "tilldate",
    label: "Till Date",
    country: [],
    merchant: [],
  });

  // 🔹 Growth data - INDEPENDENT FILTER
  const [merchantGrowthData, setMerchantGrowthData] = useState(null);
  const [merchantGrowthLoading, setMerchantGrowthLoading] = useState(false);

  // 🔹 Store data - INDEPENDENT FILTER
  const [merchantStoreData, setMerchantStoreData] = useState(null);
  const [merchantStoreLoading, setMerchantStoreLoading] = useState(false);

  // 🔹 Product data - INDEPENDENT FILTER
  const [merchantProductData, setMerchantProductData] = useState(null);
  const [merchantProductLoading, setMerchantProductLoading] = useState(false);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);

  const [merchantsStats, setMerchantsStats] = useState({});

  const [selectedDaysLabel, setSelectedDaysLabel] = useState("Last 7 days");
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const hasFetchedInitial = useRef(false);
  const fetchInProgress = useRef({
    graphData: false,
    superAdminMetrics: false,
    merchantCards: false,
    merchantGrowth: false,
    merchantStore: false,
    merchantProduct: false,
  });
  const prevParamsRef = useRef({
    graphData: null,
    cardsFilter: null,
    growthFilter: null,
    storeFilter: null,
    productFilter: null,
    customDateRange: null,
  });

  // Track if initial superadmin fetch has been done
  const superAdminInitialFetchDone = useRef(false);
  const nonSuperAdminInitialFetchDone = useRef(false);
  const isSettingInitialFilter = useRef(false);

  const stores = useSelector((state) => state.store.stores);
  const dashboardReadOnly = isProfileModuleReadOnly(user, "dashboard");
  const { RangePicker } = DatePicker;

  // Helper function to deep compare objects
  const deepCompare = useCallback((obj1, obj2) => {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }, []);

  useEffect(() => {
    if (
      !isSuperAdmin &&
      stores &&
      stores.length > 0 &&
      !selectedStore
    ) {
      setSelectedStore(stores[0]);
    }
  }, [stores, isSuperAdmin, selectedStore]);

  const fetchGraphData = async (days, startDate = null, endDate = null) => {
    // Prevent duplicate calls
    const currentParams = JSON.stringify({
      storeId: selectedStore?.id,
      days,
      startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
      endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
    });

    if (
      fetchInProgress.current.graphData ||
      prevParamsRef.current.graphData === currentParams
    ) {
      return;
    }

    fetchInProgress.current.graphData = true;
    prevParamsRef.current.graphData = currentParams;
    setLoading(true);

    try {
      const formattedStartDate = startDate
        ? dayjs(startDate).format("YYYY-MM-DD")
        : null;
      const formattedEndDate = endDate
        ? dayjs(endDate).format("YYYY-MM-DD")
        : null;

      const response = await Analytics.getProductMetrics(
        selectedStore?.id,
        days,
        formattedStartDate,
        formattedEndDate
      );
      if (response.status === 200) {
        setMetricsData(response.data);
      }
    } catch (error) {
      console.error("Error:", error);
      prevParamsRef.current.graphData = null; // Reset on error to allow retry
    } finally {
      setLoading(false);
      fetchInProgress.current.graphData = false;
    }
  };

  const fetchSuperAdminMetrics = async () => {
    // Prevent duplicate calls
    if (fetchInProgress.current.superAdminMetrics) {
      return;
    }

    fetchInProgress.current.superAdminMetrics = true;
    setLoading(true);
    try {
      const response = await Analytics.getSuperAdminMetrics();
      if (response.status === 200) {
        setMerchantsStats(response.data.data);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
      fetchInProgress.current.superAdminMetrics = false;
    }
  };

  // ✅ ONLY Cards data uses the main filter
  const fetchMerchantCardsData = async (filterOverride = cardsFilterState) => {
    const {
      filterType,
      country = [],
      merchant = [],
      startDate,
      endDate,
    } = filterOverride || {};

    const isTillDateFilter = !filterType || filterType === "tilldate";
    const effectiveStartDate = isTillDateFilter
      ? CLICKS_IMPRESSIONS_START_DATE
      : startDate || null;
    const effectiveEndDate = endDate || dayjs().format("YYYY-MM-DD");

    // Prevent duplicate calls
    const currentParams = JSON.stringify({
      filterType:
        filterType === "tilldate" ? "customdate" : filterType || "last30days",
      country: Array.isArray(country) ? country.sort().join(",") : "",
      merchant: Array.isArray(merchant) ? merchant.sort().join(",") : "",
      startDate: effectiveStartDate,
      endDate: effectiveEndDate,
    });

    if (
      fetchInProgress.current.merchantCards ||
      prevParamsRef.current.cardsFilter === currentParams
    ) {
      return;
    }

    fetchInProgress.current.merchantCards = true;
    prevParamsRef.current.cardsFilter = currentParams;

    const countriesParam =
      Array.isArray(country) && country.length > 0
        ? [...new Set(country.map((c) => String(c).toUpperCase()))].join("+")
        : null;
    const merchantParam =
      Array.isArray(merchant) && merchant.length > 0
        ? [...new Set(merchant.map((m) => String(m).trim()))].join("+")
        : null;

    setMerchantCardLoading(true);
    try {
      // Map "tilldate" to one custom-date API call with hardcoded start date.
      const apiFilterType =
        filterType === "tilldate" ? "customdate" : filterType || "last30days";
      const resp = await Analytics.getMerchantStats(
        apiFilterType,
        countriesParam,
        merchantParam,
        effectiveStartDate,
        effectiveEndDate
      );
      const parsed = resp?.data || resp || null;
      setMerchantCardData(parsed);
    } catch (e) {
      console.error("Error fetching merchant cards data:", e);
      setMerchantCardData(null);
      prevParamsRef.current.cardsFilter = null; // Reset on error to allow retry
    } finally {
      setMerchantCardLoading(false);
      fetchInProgress.current.merchantCards = false;
    }
  };

  const fetchMerchantGrowthData = async (filterConfig = "last30days") => {
    // ✅ Handle both string (legacy) and object (with custom dates) formats
    const filterType =
      typeof filterConfig === "string" ? filterConfig : filterConfig.filterType;
    const startDate =
      typeof filterConfig === "object" && filterConfig.startDate
        ? filterConfig.startDate
        : null;
    const endDate =
      typeof filterConfig === "object" && filterConfig.endDate
        ? filterConfig.endDate
        : null;

    // Prevent duplicate calls
    const currentParams = JSON.stringify({
      filterType,
      startDate: startDate || null,
      endDate: endDate || null,
    });

    if (
      fetchInProgress.current.merchantGrowth ||
      prevParamsRef.current.growthFilter === currentParams
    ) {
      return;
    }

    fetchInProgress.current.merchantGrowth = true;
    prevParamsRef.current.growthFilter = currentParams;
    setMerchantGrowthLoading(true);

    try {
      const resp = await Analytics.getMerchantAnalytics(
        filterType,
        "All",
        startDate,
        endDate
      );
      const parsed = resp?.data || resp || null;
      setMerchantGrowthData(parsed);
    } catch (e) {
      console.error("Error fetching merchant growth analytics:", e);
      setMerchantGrowthData(null);
      prevParamsRef.current.growthFilter = null; // Reset on error to allow retry
    } finally {
      setMerchantGrowthLoading(false);
      fetchInProgress.current.merchantGrowth = false;
    }
  };

  // ✅ Store data has its own filter (last30days default)
  const fetchMerchantStoreData = async (filterConfig = "last30days") => {
    // ✅ Handle both string (legacy) and object (with custom dates) formats
    const filterType =
      typeof filterConfig === "string" ? filterConfig : filterConfig.filterType;
    const startDate =
      typeof filterConfig === "object" && filterConfig.startDate
        ? filterConfig.startDate
        : null;
    const endDate =
      typeof filterConfig === "object" && filterConfig.endDate
        ? filterConfig.endDate
        : null;

    // Prevent duplicate calls
    const currentParams = JSON.stringify({
      filterType,
      startDate: startDate || null,
      endDate: endDate || null,
    });

    if (
      fetchInProgress.current.merchantStore ||
      prevParamsRef.current.storeFilter === currentParams
    ) {
      return;
    }

    fetchInProgress.current.merchantStore = true;
    prevParamsRef.current.storeFilter = currentParams;
    setMerchantStoreLoading(true);

    try {
      const resp = await Analytics.getStoreAnalytics(
        filterType,
        "All",
        startDate,
        endDate
      );
      const parsed = resp?.data || resp || null;
      setMerchantStoreData(parsed);
    } catch (e) {
      console.error("Error fetching store analytics:", e);
      setMerchantStoreData(null);
      prevParamsRef.current.storeFilter = null; // Reset on error to allow retry
    } finally {
      setMerchantStoreLoading(false);
      fetchInProgress.current.merchantStore = false;
    }
  };

  // ✅ Product data has its own filter (last30days default)
  const fetchMerchantProductData = async (filterType = "last30days") => {
    // Prevent duplicate calls
    const currentParams = JSON.stringify({ filterType });

    if (
      fetchInProgress.current.merchantProduct ||
      prevParamsRef.current.productFilter === currentParams
    ) {
      return;
    }

    fetchInProgress.current.merchantProduct = true;
    prevParamsRef.current.productFilter = currentParams;
    setMerchantProductLoading(true);

    try {
      const resp = await Analytics.getProductAnalytics(
        filterType,
        "All",
        null,
        null
      );
      const parsed = resp?.data || resp || null;
      setMerchantProductData(parsed);
    } catch (e) {
      console.error("Error fetching product analytics:", e);
      setMerchantProductData(null);
      prevParamsRef.current.productFilter = null; // Reset on error to allow retry
    } finally {
      setMerchantProductLoading(false);
      fetchInProgress.current.merchantProduct = false;
    }
  };

  // ✅ Initial load only once - SUPERADMIN
  useEffect(() => {
    if (isSuperAdmin) {
      if (superAdminInitialFetchDone.current) return;
      superAdminInitialFetchDone.current = true;
      hasFetchedInitial.current = true; // Mark as fetched to prevent cardsFilterState effect from running

      const lang = localStorage.getItem("language") || "ar";
      const today = dayjs();
      const defaultFilter = {
        startDate: CLICKS_IMPRESSIONS_START_DATE,
        endDate: today.format("YYYY-MM-DD"),
        filterType: "tilldate",
        label: lang === "ar" ? "حتى التاريخ" : "Till Date",
        country: [],
        merchant: [],
      };

      // Set initial filter params to prevent duplicate call
      const initialFilterStr = JSON.stringify({
        filterType:
          defaultFilter.filterType === "tilldate"
            ? "customdate"
            : defaultFilter.filterType,
        country: [],
        merchant: [],
        startDate: defaultFilter.startDate,
        endDate: defaultFilter.endDate,
      });
      prevParamsRef.current.cardsFilter = initialFilterStr;

      // Set flag to prevent cardsFilterState effect from running
      isSettingInitialFilter.current = true;
      setCardsFilterState(defaultFilter);

      // Fetch all superadmin data
      fetchSuperAdminMetrics();
      fetchMerchantCardsData(defaultFilter);
      fetchMerchantGrowthData("last30days");
      fetchMerchantStoreData("last30days");
      fetchMerchantProductData("last30days");

      // Reset flag after a short delay to allow state update
      setTimeout(() => {
        isSettingInitialFilter.current = false;
      }, 100);
    }
  }, [isSuperAdmin]);

  // ✅ Track selectedStore changes to reset params when store changes
  const prevStoreIdRef = useRef(null);

  useEffect(() => {
    if (!isSuperAdmin && selectedStore) {
      // If store changed, reset the graph data params to allow new fetch
      if (prevStoreIdRef.current !== selectedStore?.id) {
        prevStoreIdRef.current = selectedStore?.id;
        prevParamsRef.current.graphData = null; // Reset to allow fetch with new store
      }
    }
  }, [selectedStore, isSuperAdmin]);

  // ✅ Initial load and updates for NON-SUPERADMIN
  useEffect(() => {
    if (!isSuperAdmin && selectedStore && selectedDays) {
      // Skip if custom date range is active (handled by another effect)
      if (selectedDaysLabel === "Custom Range") {
        return;
      }

      const currentParams = JSON.stringify({
        storeId: selectedStore?.id,
        days: selectedDays,
      });

      // Prevent duplicate calls
      if (prevParamsRef.current.graphData === currentParams) {
        return;
      }

      // The fetchGraphData function will set prevParamsRef.current.graphData
      // so we don't set it here to avoid race conditions
      fetchGraphData(selectedDays);
    }
  }, [highest_role, selectedStore, selectedDays, selectedDaysLabel]);

  // ✅ Memoize cardsFilterState serialized value to prevent unnecessary re-renders
  const cardsFilterStateStr = useMemo(() => {
    const countryStr = Array.isArray(cardsFilterState.country)
      ? [...cardsFilterState.country].sort().join(",")
      : "";
    const merchantStr = Array.isArray(cardsFilterState.merchant)
      ? [...cardsFilterState.merchant].sort().join(",")
      : "";

    return JSON.stringify({
      filterType:
        cardsFilterState.filterType === "tilldate"
          ? "customdate"
          : cardsFilterState.filterType || "last30days",
      country: countryStr,
      merchant: merchantStr,
      startDate: cardsFilterState.startDate || null,
      endDate: cardsFilterState.endDate || null,
    });
  }, [
    cardsFilterState.filterType,
    // Serialize arrays for stable comparison
    Array.isArray(cardsFilterState.country)
      ? [...cardsFilterState.country].sort().join(",")
      : "",
    Array.isArray(cardsFilterState.merchant)
      ? [...cardsFilterState.merchant].sort().join(",")
      : "",
    cardsFilterState.startDate,
    cardsFilterState.endDate,
  ]);

  // ✅ Re-fetch ONLY cards data when main filter changes (deep comparison)
  useEffect(() => {
    // Skip if we're setting the initial filter
    if (isSettingInitialFilter.current) {
      return;
    }

    // Only run after initial fetch is done
    if (
      isSuperAdmin &&
      superAdminInitialFetchDone.current &&
      hasFetchedInitial.current
    ) {
      const prevFilterStr = prevParamsRef.current.cardsFilter;

      // Only fetch if filter actually changed
      if (prevFilterStr && prevFilterStr !== cardsFilterStateStr) {
        fetchMerchantCardsData(cardsFilterState);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsFilterStateStr, isSuperAdmin]);

  // ✅ Handle custom date range for non-superadmin (with debounce)
  useEffect(() => {
    if (
      !isSuperAdmin &&
      selectedStore &&
      selectedDaysLabel === "Custom Range" &&
      customDateRange.startDate &&
      customDateRange.endDate
    ) {
      const currentParams = JSON.stringify({
        storeId: selectedStore?.id,
        startDate: customDateRange.startDate,
        endDate: customDateRange.endDate,
      });

      // Prevent duplicate calls
      if (prevParamsRef.current.customDateRange === currentParams) {
        return;
      }

      // Debounce the API call
      const handler = setTimeout(() => {
        prevParamsRef.current.customDateRange = currentParams;
        prevParamsRef.current.graphData = currentParams; // Update graph data params too
        fetchGraphData(
          null,
          customDateRange.startDate,
          customDateRange.endDate
        );
      }, 500); // Increased debounce time

      return () => clearTimeout(handler);
    }
  }, [customDateRange, selectedDaysLabel, selectedStore, isSuperAdmin]);

  const handleDropdownChange = (value) => {
    if (value === "Custom Range") {
      setSelectedDays(null);
      setSelectedDaysLabel(value);
      // Reset custom date range params when switching to custom range
      prevParamsRef.current.customDateRange = null;
      return;
    }

    const daysMap = {
      "Last 7 days": 7,
      "Last 30 days": 30,
      "Last 90 days": 90,
    };

    // Reset custom date range and its params when switching away from custom range
    setCustomDateRange({ startDate: null, endDate: null });
    prevParamsRef.current.customDateRange = null;
    setSelectedDays(daysMap[value]);
    setSelectedDaysLabel(value);
  };

  // Prevent rapid filter changes
  const filterSelectTimeoutRef = useRef(null);
  const prevFilterRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (filterSelectTimeoutRef.current) {
        clearTimeout(filterSelectTimeoutRef.current);
      }
    };
  }, []);

  // ✅ Handle filter selection ONLY for cards (with debounce)
  const handleFilterSelect = useCallback((filter) => {
    // Serialize filter to compare
    const filterStr = JSON.stringify({
      filterType:
        filter?.filterType === "tilldate"
          ? "customdate"
          : filter?.filterType || "last30days",
      country: Array.isArray(filter?.country)
        ? [...filter.country].sort().join(",")
        : "",
      merchant: Array.isArray(filter?.merchant)
        ? [...filter.merchant].sort().join(",")
        : "",
      startDate: filter?.startDate || null,
      endDate: filter?.endDate || null,
    });

    // Skip if same filter
    if (prevFilterRef.current === filterStr) {
      return;
    }

    // Clear previous timeout
    if (filterSelectTimeoutRef.current) {
      clearTimeout(filterSelectTimeoutRef.current);
    }

    // Debounce the filter update
    filterSelectTimeoutRef.current = setTimeout(() => {
      prevFilterRef.current = filterStr;
      setCardsFilterState(filter);
    }, 300);
  }, []);

  const disabledDate = (current) => {
    if (!current) return false;
    const today = dayjs().endOf("day");
    const oneYearAgo = dayjs().subtract(1, "year").startOf("day");
    return current > today || current < oneYearAgo;
  };

  const normalizePath = (path) => {
    if (!path) return "/";
    const trimmed = path.replace(/\/+$/, "");
    return trimmed === "" ? "/" : trimmed;
  };
  const currentPath = normalizePath(location.pathname);

  return (
    <div>
      <div className="d-flex justify-content-between mb-18">
        <div
          className="d-flex justify-content-between align-items-center w-100"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: "100%",
            paddingBottom: "15px",
          }}
        >
          <h1
            className="text-141414 text-20 font-600 m-0"
            style={{
              display: "flex",
              alignItems: "center",
              margin: 0,
            }}
          >
            {currentLanguage?.welcome}{" "}
            {lang === "ar" ? user?.name_arabic : user?.name_english}
          </h1>

          <div
            className="d-flex align-items-center"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "wrap",
              height: "100%",
            }}
          >
            {isSuperAdmin && (
              <>
                <div className="nav-switch">
                  <button
                    className={`navbar-tab ${
                      currentPath === "/dashboard/merchant-analytics"
                        ? "active"
                        : ""
                    }`}
                    onClick={() => navigate("/dashboard/merchant-analytics")}
                  >
                    {lang === "ar" ? "التجار" : "Merchant"}
                  </button>
                  <button
                    className={`navbar-tab ${
                      currentPath === "/dashboard" ? "active" : ""
                    }`}
                    onClick={() => navigate("/dashboard")}
                  >
                    {lang === "ar" ? "تقرير الذكاء الاصطناعي" : "AI Dashboard"}
                  </button>
                </div>
                {/* ✅ Date/country filters remain usable in view-only (read-only) dashboard access */}
                <FilterforMerchants
                  currentLanguage={currentLanguage}
                  onFilterSelect={handleFilterSelect}
                  initialFilterMeta={merchantCardData?.result}
                />
              </>
            )}
          </div>
        </div>

        {!isSuperAdmin && (
          <div
            className="select-store"
            onClick={
              dashboardReadOnly ? undefined : () => setDrawerVisible(true)
            }
            style={
              dashboardReadOnly
                ? { cursor: "not-allowed", opacity: 0.65 }
                : undefined
            }
            title={
              dashboardReadOnly
                ? lang === "ar"
                  ? "عرض فقط"
                  : "View only"
                : undefined
            }
          >
            <p className={`mb-0 ${lang === "ar" ? "ml-10" : "mr-10"}`}>
              {lang === "ar"
                ? selectedStore?.store_name_arabic
                : selectedStore?.store_name ||
                  currentLanguage?.select_store}{" "}
            </p>
            <img
              src="/icons/arrowSelectionDark.svg"
              style={{
                transform: lang === "ar" ? "rotateY(180deg)" : "none",
              }}
              alt=""
            />
          </div>
        )}
      </div>

      {/* ✅ Cards use the main filter */}
      {isSuperAdmin && (
        <MerAnalyticsCards
          data={merchantCardData}
          loading={merchantCardLoading}
        />
      )}

      {/* ✅ Charts use the same data as cards */}
      {isSuperAdmin && (
        <MerAnalyticsCharts
          data={merchantCardData}
          loading={merchantCardLoading}
        />
      )}

      {/* ✅ Growth/Store/Product have INDEPENDENT filters */}
      {isSuperAdmin && (
        <MerchantGrowthTrend
          grouthData={merchantGrowthData}
          merchantStoreData={merchantStoreData}
          merchantProductData={merchantProductData}
          loading={
            merchantGrowthLoading ||
            merchantStoreLoading ||
            merchantProductLoading
          }
          onMerchantGrowthFilter={(filterType) => {
            fetchMerchantGrowthData(filterType);
          }}
          onMerchantStoreFilter={(filterType) => {
            fetchMerchantStoreData(filterType);
          }}
          onMerchantProductFilter={(filterType) => {
            fetchMerchantProductData(filterType);
          }}
        />
      )}

      {isSuperAdmin ? (
        loading ? (
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <Spin size="large" />
          </div>
        ) : (
          ""
        )
      ) : null}

      {!isSuperAdmin && (
        <div className="graph-container">
          <div style={{ padding: "20px", width: "100%", height: "100%" }}>
            {!isSuperAdmin ? (
              <AreaChartWithZoom
                Title={currentLanguage?.Analytics}
                selectedStore={selectedStore}
              />
            ) : (
              <BusinessMetricsChart Title={dashboardAdmin.new_merchants} />
            )}
          </div>

          {isSuperAdmin && (
            <div
              style={{
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "20px",
              }}
            >
              <MerchantSubscription />
            </div>
          )}
        </div>
      )}

      {!isSuperAdmin && (
        <>
          <div>
            <div className="d-flex justify-content-between mb-12 dropdownWithCustomDateContainer">
              <h2 className="text-20 font-600 text-141414">
                {currentLanguage?.metrics}
              </h2>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {selectedDaysLabel === "Custom Range" && (
                  <div className="custom-datepicker-wrapper">
                    <RangePicker
                      allowClear={false}
                      disabledDate={(current) => {
                        if (!current) return false;
                        const today = dayjs().endOf("day");
                        const oneYearAgo = dayjs()
                          .subtract(1, "year")
                          .startOf("day");
                        return current > today || current < oneYearAgo;
                      }}
                      placement="bottomLeft"
                      value={
                        customDateRange.startDate && customDateRange.endDate
                          ? [
                              dayjs(customDateRange.startDate),
                              dayjs(customDateRange.endDate),
                            ]
                          : []
                      }
                      onChange={(dates) => {
                        if (dates && dates.length === 2) {
                          const [start, end] = dates;
                          setCustomDateRange({
                            startDate: start.toISOString(),
                            endDate: end.toISOString(),
                          });
                        }
                      }}
                    />
                  </div>
                )}
                <Dropdown
                  selectedCategory={selectedDaysLabel}
                  width="160px"
                  label={currentLanguage?.dropdown_1?.last_7_days}
                  iconSrc="/icons/selectstoreanalytics.svg"
                  options={[
                    {
                      label: currentLanguage?.dropdown_1?.last_7_days,
                      value: "Last 7 days",
                    },
                    {
                      label: currentLanguage?.dropdown_1?.last_30_days,
                      value: "Last 30 days",
                    },
                    {
                      label: currentLanguage?.dropdown_1?.last_90_days,
                      value: "Last 90 days",
                    },
                    {
                      label: currentLanguage?.chart?.custom_range,
                      value: "Custom Range",
                    },
                  ]}
                  onSelect={handleDropdownChange}
                />
              </div>
            </div>

            <div className="click-impression-container">
              <div className="row">
                <div className="col-4">
                  <ClickImpressionBox
                    loading={loading}
                    data={metricsData?.total_clicks}
                    title={currentLanguage?.total_clicks}
                    lang={lang}
                    currentLanguage={currentLanguage}
                  />
                  <div className="mb-16"></div>
                  <ClickImpressionBox
                    loading={loading}
                    data={metricsData?.total_views}
                    title={currentLanguage?.total_impressions}
                    lang={lang}
                    currentLanguage={currentLanguage}
                  />
                </div>

                <div className="col-8">
                  <div className="most-viewed">
                    <p className="text-14 text-2E2E30 font-500 mb-12">
                      {currentLanguage?.most_viewed}
                    </p>
                    <div className="overflow-auto" style={{ height: "225px" }}>
                      {loading ? (
                        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                          <Spin size="large" />
                        </div>
                      ) : metricsData?.top_10_products?.length > 0 ? (
                        metricsData?.top_10_products?.map((item) => (
                          <MostViewedItem
                            key={item.id}
                            item={item}
                            lang={lang}
                          />
                        ))
                      ) : !selectedStore ? (
                        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                          <p>
                            {currentLanguage?.select_store_to_view_analytics}
                          </p>
                        </div>
                      ) : (
                        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                          {currentLanguage?.no_data_right}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <StoreDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            stores={stores}
            selectedStore={selectedStore}
            onSelectStore={setSelectedStore}
            AllFilterOption={currentLanguage?.stores}
          />
        </>
      )}
    </div>
  );
};

export default MerchantDashboard;
